import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MaterialModule } from '../../material.module';
import { SearchModule } from '../search/search.module';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ITableProp } from '../../models/tables.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  gridMessages,
  lookupHeaders,
  lookupPluralHeaders,
} from '../../utility/constants';

@Component({
  selector: 'app-multi-lookup-popup',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    MaterialModule,
    SearchModule,
    ScrollingModule,
    FormsModule,
  ],
  templateUrl: './multi-lookup-popup.component.html',
  styleUrl: './multi-lookup-popup.component.scss',
})
export class MultiLookupPopupComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  public crudPopupData: any;
  public searchTextbox = '';
  public lookupTableDataSource = new MatTableDataSource<any>([]);
  public lookupTableDisplayedColumns!: string[];
  public lookupTableColumns!: ITableProp[];
  public selectedItems: any[] = [];
  public noResultsMessage = '';

  /**
   * Opt-in flags (SPEC-CRS-001-v1.0 §9). Every one of them defaults to the
   * behaviour the four existing `openMultiLookupPopup` callers already get, so
   * country / state / origin / destination lookups are untouched.
   */
  public popupTitle = '';
  public searchPlaceholder = '';
  public showSelectAll = false;
  public readOnly = false;
  public sortable = false;
  public tableSortDirection = 'desc';

  private selectedDisplayKey = '';
  private allData: any[] = [];

  /** Rows appended per scroll-to-bottom (SPEC-CRS-001-v1.1 FR-CRS-UI-036). */
  private readonly pageSize = 100;
  private currentIndex = this.pageSize;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ref: MatDialogRef<MultiLookupPopupComponent>,
    private readonly cdref: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.crudPopupData = this.data;
    this.lookupTableDisplayedColumns =
      this.crudPopupData.code.displayedColumnsList;
    this.lookupTableColumns = this.crudPopupData.code.tableColumns;
    this.allData = this.crudPopupData.code.tableData;
    this.applyOptionalConfig();
    this.getnoMatchMessage();
    this.lookupTableDataSource.data = this.allData.slice(0, this.pageSize);
    this.currentIndex = this.pageSize;

    if (this.crudPopupData.code.selectedOptionData?.length) {
      this.selectedItems = [...this.crudPopupData.code.selectedOptionData];
    }
  }

  ngAfterViewInit(): void {
    if (this.sortable && this.sort) {
      this.sort.active = this.lookupTableDisplayedColumns[0];
      this.lookupTableDataSource.sort = this.sort;
      this.cdref.detectChanges();
    }
  }

  /** Confirm and send selection back */
  public onConfirm() {
    this.ref.close({
      popupClose: true,
      data: this.selectedItems,
    });
  }

  /** Cancel without saving */
  public onCancel() {
    this.ref.close({ popupClose: false });
  }

  /** Toggle selection on row button */
  public toggleSelection(row: any) {
    const index = this.selectedItems.findIndex(x => x.id === row.id);
    if (index > -1) {
      this.selectedItems.splice(index, 1);
    } else {
      this.selectedItems.push(row);
    }
  }

  /** Whether row is selected */
  public isSelected(row: any): boolean {
    return this.selectedItems.some(x => x.id === row.id);
  }

  /** Clear all selections */
  public clearAll() {
    this.selectedItems = [];
  }

  /**
   * Select every row the popup was opened with — not just the rows currently
   * visible after a search. Only rendered when `showSelectAll` is set.
   */
  public selectAll() {
    this.selectedItems = [...this.allData];
  }

  /** Hides **Select All** once there is nothing left for it to select. */
  public isAllSelected(): boolean {
    return (
      this.allData.length > 0 &&
      this.selectedItems.length >= this.allData.length
    );
  }

  /** Filter list */
  public filterLookUpList() {
    this.lookupTableDataSource.data = this.filterDataBasedOnSearch().slice(
      0,
      this.pageSize
    );
    this.currentIndex = this.pageSize;
  }

  /**
   * Appends the next page once the viewport reaches the bottom
   * (FR-CRS-UI-036). Without this the table renders only the first page and
   * every row beyond it is unreachable except through the search box.
   */
  public onScroll() {
    if (!this.viewport.measureScrollOffset('bottom')) {
      const filteredData = this.filterDataBasedOnSearch();
      if (this.currentIndex < filteredData.length) {
        const nextData = filteredData.slice(
          this.currentIndex,
          this.currentIndex + this.pageSize
        );
        this.lookupTableDataSource.data = [
          ...this.lookupTableDataSource.data,
          ...nextData,
        ];
        this.currentIndex += this.pageSize;
      }
    }
  }

  private filterDataBasedOnSearch() {
    const modifiedSearchValue =
      this.searchTextbox.trim() !== ''
        ? this.searchTextbox.replace(/[\s-]/g, '')?.toLowerCase()
        : '';
    if (!modifiedSearchValue) {
      return this.allData;
    }
    return this.allData.filter((opt: any) => {
      const fieldValue: string =
        opt[this.lookupTableDisplayedColumns[0]]?.toLowerCase() ?? '';
      // Split on ' - ' so code and name are checked independently,
      // then verify that either part *starts with* the search term.
      return fieldValue
        .split(' - ')
        .some(part =>
          part.replace(/[\s-]/g, '').startsWith(modifiedSearchValue)
        );
    });
  }

  public getSelectedNames(): string {
    if (this.selectedDisplayKey) {
      return this.selectedItems
        .map(item => item[this.selectedDisplayKey])
        .join(', ');
    }
    return this.selectedItems
      .map(item => `${item.id} - ${item.name}`)
      .join(', ');
  }
  public getMatchedTableRow(rowData: any) {
    return (
      rowData[this.lookupTableDisplayedColumns[0]] ===
      this.crudPopupData.code.selectedOptionData
    );
  }
  public getPopupTitle(): string {
    return this.popupTitle || `${this.crudPopupData.title} Lookup`;
  }

  public getPlaceholderText(): string {
    return this.searchPlaceholder || `Search ${this.crudPopupData.title}`;
  }

  private applyOptionalConfig() {
    const code = this.crudPopupData.code;
    this.popupTitle = code.popupTitle ?? '';
    this.searchPlaceholder = code.searchPlaceholder ?? '';
    this.showSelectAll = code.showSelectAll === true;
    this.readOnly = code.readOnly === true;
    this.sortable = code.sortable === true;
    this.selectedDisplayKey = code.selectedDisplayKey ?? '';
    if (code.noMatchMessage) {
      this.noResultsMessage = code.noMatchMessage;
    }
  }

  private getnoMatchMessage() {
    if (this.noResultsMessage) {
      return;
    }
    if (this.crudPopupData.title === lookupHeaders.destination) {
      this.noResultsMessage = gridMessages.noRecordsMatch(
        lookupPluralHeaders.countries
      );
    }
  }
  public clearSearch(): void {
    this.searchTextbox = '';
    this.filterLookUpList();
  }
  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.clearSearch();
    }
  }
}
